import {
  ClerkLoaded,
  ClerkLoading,
  SignedIn,
  SignedOut,
  useAuth,
  useClerk,
} from "@clerk/clerk-react";
import { lazy, Suspense, useEffect, useRef } from "react";
import { BrowserRouter, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { PendingConsentRoutes } from "@/components/legal/PendingConsentRoutes";
import { AccountThemeSync } from "@/hooks/use-account-theme";
import { AccountTimezoneSync } from "@/hooks/use-account-timezone";
import {
  setAuthTokenProvider,
  setSessionExpiredHandler,
  setSubscriptionRequiredHandler,
} from "@/api/client";
import { subscriptionRequiredRedirect } from "@/lib/subscriptionGate";
import { loginPathFor } from "@/lib/authRedirect";
import { SignInRedirect } from "@/components/auth/SignInRedirect";
import { queryClient } from "@/lib/queryClient";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import AuthLayout from "@/components/layout/AuthLayout";
import Landing from "@/pages/Landing";

/* ── Route-level code splitting (#324) ────────────────────────────────────────
 *
 * Landing is the only page imported eagerly, because it is what "/" renders for
 * a signed-out visitor and it is the first paint we are measured on. Every
 * other route is a `lazy()` import, which is what actually keeps Recharts
 * (Dashboard, Forecast, Insights, Financial Analysis), react-plaid-link
 * (Onboarding, Banks) and the Clerk sign-in surfaces out of the initial bundle.
 *
 * Splitting /features alone did not: the note that used to sit here said as
 * much, because Dashboard and Insights imported Recharts eagerly a few lines
 * below and pulled it straight back in. Measured on `develop@8c1de82`, the main
 * chunk was 2,110.73 kB raw / 601.66 kB gzip.
 *
 * Every one of these renders inside <RouteChunk>, so the Suspense boundary sits
 * at the leaf: the layout that owns the page stays mounted while its chunk
 * arrives, and only the content area is briefly empty.
 */
const Features = lazy(() => import("@/pages/Features"));
const Contact = lazy(() => import("@/pages/Contact"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const DeleteAccount = lazy(() => import("@/pages/DeleteAccount"));
const WaitlistConfirm = lazy(() => import("@/pages/WaitlistConfirm"));
const WaitlistUnsubscribe = lazy(() => import("@/pages/WaitlistUnsubscribe"));
const DemoAccess = lazy(() => import("@/pages/DemoAccess"));
const Login = lazy(() => import("@/pages/auth/Login"));
const Signup = lazy(() => import("@/pages/auth/Signup"));
const ForgotPassword = lazy(() => import("@/pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/auth/ResetPassword"));
const SSOCallback = lazy(() => import("@/pages/auth/SSOCallback"));
const SSOComplete = lazy(() => import("@/pages/auth/SSOComplete"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Home = lazy(() => import("@/pages/Home"));
const Plan = lazy(() => import("@/pages/Plan"));
const Transactions = lazy(() => import("@/pages/Transactions"));
const Rules = lazy(() => import("@/pages/Rules"));
const Budgets = lazy(() => import("@/pages/Budgets"));
const Forecast = lazy(() => import("@/pages/Forecast"));
const Calendar = lazy(() => import("@/pages/Calendar"));
const RecurringTransactions = lazy(() => import("@/pages/RecurringTransactions"));
const DebtEliminator = lazy(() => import("@/pages/DebtEliminator"));
const PaycheckRemainder = lazy(() => import("@/pages/PaycheckRemainder"));
const Settings = lazy(() => import("@/pages/Settings"));
const Help = lazy(() => import("@/pages/Help"));
const Onboarding = lazy(() => import("@/pages/onboarding/Onboarding"));
const BankManagement = lazy(() => import("@/pages/BankManagement"));
const Goals = lazy(() => import("@/pages/Goals"));
const Insights = lazy(() => import("@/pages/Insights"));
const FinancialAnalysis = lazy(() => import("@/pages/FinancialAnalysis"));
const Payment = lazy(() => import("@/pages/Payment"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PlaidOAuthReturn = lazy(() => import("@/pages/PlaidOAuthReturn"));
const NotFound = lazy(() => import("@/pages/NotFound"));
import {
  isOnboardingComplete,
  markOnboardingComplete,
  clearOnboardingComplete,
  onboardingGateDecision,
} from "@/lib/onboarding";
import { SessionBootstrapFallback } from "@/components/auth/SessionBootstrap";
import { onboardingGateElement } from "@/lib/onboardingGateSurface";
import { landingPathFor } from "@/lib/homeIntro";
import { useRedesignNavState } from "@/lib/redesignNav";
import { RedesignNavProvider } from "@/components/redesign/RedesignNavProvider";
import { redesignRedirectFor, retiredRedirectFor } from "@/lib/redesignRoutes";
import { useProfile } from "@/hooks/use-profile";
import { useAppDataPrefetch } from "@/hooks/use-app-data-prefetch";
import { PreferencesProvider } from "@/components/PreferencesProvider";
import FeedbackWidget from "@/components/feedback/FeedbackWidget";
import { PendingDeletedNotice } from "@/components/PendingDeletedNotice";
import { WalkthroughHost } from "@/components/walkthrough/WalkthroughHost";

// Root "/" — signed-in + onboarded users land on their preferred default view
// (Settings → "Default view on open"); everyone else gets the public landing.
function RootRedirect() {
  const { isSignedIn, isLoaded } = useAuth();
  const profileQuery = useProfile({ enabled: isLoaded === true && isSignedIn === true });
  if (!isLoaded) return <SessionBootstrapFallback />;
  if (!isSignedIn) return <Landing />;
  // A signed-in user whose profile did not load used to fall through to the
  // marketing page: measured on the deployed dev host with every `/api/**`
  // request forced to 500, "/" rendered 2,409 characters of Landing and said
  // nothing about the failure (BEN-141 QF8). /dashboard is where the shell
  // reports it, with a retry, so send them there rather than to the front
  // door of a product they have already bought. Checked before `isPending`
  // for the same reason the gate carries `hasFailed`: a retry in flight is
  // still a failure, not a fresh unanswered request.
  if (profileQuery.errorUpdateCount > 0) return <Navigate to="/dashboard" replace />;
  if (profileQuery.isPending) return null;
  const profile = profileQuery.data?.profile;
  if (profile?.onboarding_complete && profile.default_view) {
    // With the redesign switch on, the stored value "dashboard" means Home (Home
    // replaced the Dashboard as the landing page); the other two values are unchanged.
    // Off, it is today's landing exactly.
    const redesign = profileQuery.data?.features?.redesign_nav === true;
    return (
      <Navigate
        to={redesign ? landingPathFor(profile.default_view) : `/${profile.default_view}`}
        replace
      />
    );
  }
  return <Landing />;
}

/**
 * A page whose address the redesign moves or retires (`lib/redesignRoutes`). With the
 * switch on, the old address redirects to the new one, and a retired page (Allocate,
 * Budgets, Goals) lands on Plan; off, a new address redirects back; either way the query
 * string is kept (e.g. `?welcome=1`). While the switch is unknown it renders nothing, so
 * nobody sees the wrong page for a moment.
 */
function RedesignAwareRoute({ children }: { children: React.ReactNode }) {
  const { pathname, search } = useLocation();
  const state = useRedesignNavState();
  if (state === "unknown") return null;
  const on = state === "on";
  const gone = retiredRedirectFor(pathname, search, on);
  if (gone) return <Navigate to={gone} replace />;
  const target = redesignRedirectFor(pathname, on);
  if (target) return <Navigate to={`${target}${search}`} replace />;
  return <>{children}</>;
}

/**
 * Suspense boundary for a code-split route (#324).
 *
 * The fallback is `null` on purpose, and it is the app's existing answer to
 * "we do not know yet": OnboardingGate returns `null` while the profile is in
 * flight, and /features has rendered under `fallback={null}` since it was
 * split. A chunk resolves in a frame or two on a warm cache, so a spinner would
 * mostly be a flash. Because the boundary is at the leaf, the layout around the
 * page — sidebar, header, back-to-top — stays mounted while the chunk arrives.
 */
function RouteChunk({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={null}>{children}</Suspense>;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  return (
    <>
      {/* Clerk's own <SignedIn>/<SignedOut> both render nothing until the SDK
          loads, so an SDK that never loads leaves this route blank forever
          (BEN-141 QF4). ClerkLoading is where that wait becomes visible. */}
      <ClerkLoading>
        <SessionBootstrapFallback />
      </ClerkLoading>
      <ClerkLoaded>
        <SignedIn>{children}</SignedIn>
        <SignedOut>
          <SignInRedirect />
        </SignedOut>
      </ClerkLoaded>
    </>
  );
}

function ApiSessionExpiryBridge() {
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const redirectingRef = useRef(false);

  useEffect(() => {
    setSessionExpiredHandler((returnTo) => {
      if (redirectingRef.current || window.location.pathname.startsWith("/auth/")) {
        return;
      }

      redirectingRef.current = true;
      const loginPath = loginPathFor(returnTo);
      void signOut({ redirectUrl: loginPath }).catch(() => {
        // If Clerk cannot clear its remote session, still leave the failed
        // protected route. Login already retries after clearing stale state.
        try {
          navigate(loginPath, { replace: true });
        } finally {
          // A client-side fallback does not remount the provider, so allow a
          // later authenticated session to use the bridge again.
          redirectingRef.current = false;
        }
      });
    });

    return () => setSessionExpiredHandler(null);
  }, [navigate, signOut]);

  return null;
}

/**
 * CALL-04 (2026-10-26): a 402 from any API lands on Pick your plan, whatever page fired it.
 * The paywall gate decides from the status call; this is the backstop for every other
 * path (a gate that rendered while the status call was failing, a page outside the
 * gated layout). Open pages (Settings, Help, Legal, Payment) stay put.
 */
function SubscriptionRequiredBridge() {
  const navigate = useNavigate();

  useEffect(() => {
    setSubscriptionRequiredHandler((location) => {
      const to = subscriptionRequiredRedirect(location);
      if (to) navigate(to, { replace: true });
    });
    return () => setSubscriptionRequiredHandler(null);
  }, [navigate]);

  return null;
}

/** Routes an authenticated user must reach regardless of onboarding state.
 *
 * Stripe redirects back to `/payment/success` (success_url) and `/payment`
 * (cancel_url). Those land inside this gate, so a user who has not finished the
 * wizard was bounced to `/onboarding` and never saw the confirmation — having
 * already handed over a card. Billing is account-level, not app-data, so it is
 * exempt. The Plaid OAuth return is also exempt because it can occur during
 * onboarding; every other gated route still requires completed onboarding.
 *
 * `/settings` too (FP-169, 2026-10-07): it holds "Delete my account", and a new
 * account that wanted to leave was sent through the setup wizard first (Paul, on the
 * throwaway walk: Settings, then the tour, then "continue without forecast", then
 * Settings again). Deleting an account must not cost more than creating one. The
 * subscription gate already leaves /settings open (`subscriptionGate.ts`).
 *
 * `/help` too (paywall rehearsal, report 142): a new account in setup could not open
 * Help; it was sent back to setup. The subscription gate already leaves it open. */
const ONBOARDING_EXEMPT_PREFIXES = ["/payment", "/banks/oauth-return", "/settings", "/help"];

function OnboardingGate() {
  const { isSignedIn, isLoaded } = useAuth();
  const { pathname } = useLocation();
  // Before the gate's own early returns: everything the shell and the page need
  // shares one dependency (the session), and the tree used to serialise them
  // into three rounds because this component renders nothing until the profile
  // lands. See `hooks/use-app-data-prefetch.ts`.
  useAppDataPrefetch(isLoaded === true && isSignedIn === true);
  const profileQuery = useProfile({ enabled: isLoaded === true && isSignedIn === true });
  // Rules sits under /settings with the redesign on (RD-07); it is a paid page that
  // needs a finished setup, so the /settings exemption does not reach it.
  const billingRoute =
    !(pathname === "/settings/rules" || pathname.startsWith("/settings/rules/")) &&
    ONBOARDING_EXEMPT_PREFIXES.some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
    );

  // Sync localStorage to server value once profile resolves so future renders
  // (or cold loads) start with the correct optimistic hint.
  useEffect(() => {
    if (profileQuery.data) {
      if (profileQuery.data.profile.onboarding_complete) {
        markOnboardingComplete();
      } else {
        clearOnboardingComplete();
      }
    }
  }, [profileQuery.data]);

  if (!isLoaded) {
    return <SessionBootstrapFallback />;
  }

  if (!isSignedIn) {
    return <SignInRedirect />;
  }

  // While the profile is fetching, the localStorage hint avoids gate flicker;
  // the effect above overwrites the cache once the server result lands. A
  // failed fetch is no longer read as "new user": the gate says the account
  // could not be loaded instead of restarting onboarding for someone who
  // finished it months ago (BEN-141 QF4).
  const outcome = onboardingGateDecision({
    status: profileQuery.isPending ? "pending" : profileQuery.isError ? "error" : "success",
    hasFailed: profileQuery.errorUpdateCount > 0,
    onboardingComplete: profileQuery.isPending
      ? isOnboardingComplete()
      : (profileQuery.data?.profile.onboarding_complete ?? false),
    billingRoute,
  });

  return onboardingGateElement(outcome, {
    error: profileQuery.error,
    onRetry: () => void profileQuery.refetch(),
  });
}

export default function App() {
  const { getToken } = useAuth();

  useEffect(() => {
    setAuthTokenProvider(() => getToken());
  }, [getToken]);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter>
          <ApiSessionExpiryBridge />
          <SubscriptionRequiredBridge />
          <AccountThemeSync />
          <AccountTimezoneSync />
          <WalkthroughHost />
          <PreferencesProvider>
            <RedesignNavProvider>
              {/* G42: a new account's unanswered "Review our terms" prompt, in place of every page. */}
              <PendingConsentRoutes>
                {/* Root: default-view redirect for signed-in users, else landing */}
                <Route index element={<RootRedirect />} />

                {/* Public marketing pages — same shell as the landing
                (MarketingLayout). */}
                <Route
                  path="features"
                  element={
                    <RouteChunk>
                      <Features />
                    </RouteChunk>
                  }
                />

                {/* Public contact page. NOT the same thing as /help, which is
                  inside OnboardingGate and unreachable to a marketing visitor;
                  this is the footer's Contact destination (BEN-141 item 4). */}
                <Route
                  path="contact"
                  element={
                    <RouteChunk>
                      <Contact />
                    </RouteChunk>
                  }
                />

                {/* Public legal pages */}
                <Route
                  path="privacy"
                  element={
                    <RouteChunk>
                      <Privacy />
                    </RouteChunk>
                  }
                />
                <Route
                  path="terms"
                  element={
                    <RouteChunk>
                      <Terms />
                    </RouteChunk>
                  }
                />
                <Route
                  path="legal/privacy"
                  element={
                    <RouteChunk>
                      <Privacy />
                    </RouteChunk>
                  }
                />
                <Route
                  path="legal/terms"
                  element={
                    <RouteChunk>
                      <Terms />
                    </RouteChunk>
                  }
                />

                {/* Account deletion instructions. PUBLIC on purpose: Google Play
                  requires a URL it can show on the store listing, and a
                  reviewer opens it signed out. The in-app control lives behind
                  OnboardingGate, so it cannot serve as that URL. */}
                <Route
                  path="delete-account"
                  element={
                    <RouteChunk>
                      <DeleteAccount />
                    </RouteChunk>
                  }
                />

                {/* Waitlist double-opt-in confirmation target */}
                <Route
                  path="waitlist/confirm"
                  element={
                    <RouteChunk>
                      <WaitlistConfirm />
                    </RouteChunk>
                  }
                />
                {/* Waitlist removal target, linked from the same email */}
                <Route
                  path="waitlist/unsubscribe"
                  element={
                    <RouteChunk>
                      <WaitlistUnsubscribe />
                    </RouteChunk>
                  }
                />

                {/* Unlinked direct-URL demo access (demo removed from public nav, feedback #2) */}
                <Route
                  path="demo"
                  element={
                    <RouteChunk>
                      <DemoAccess />
                    </RouteChunk>
                  }
                />

                {/* Sign-up page 1 sits in its own frame, the sign-up shell (Anthony's doc 3:
                    the logo and "Step 1 of 4" only, the step bar, the card), not the split
                    auth layout the other auth screens use. */}
                <Route
                  path="auth/signup"
                  element={
                    <RouteChunk>
                      <Signup />
                    </RouteChunk>
                  }
                />

                {/* Auth screens — own layout */}
                <Route element={<AuthLayout />}>
                  <Route
                    path="auth/login"
                    element={
                      <RouteChunk>
                        <Login />
                      </RouteChunk>
                    }
                  />
                  <Route
                    path="auth/forgot-password"
                    element={
                      <RouteChunk>
                        <ForgotPassword />
                      </RouteChunk>
                    }
                  />
                  <Route
                    path="auth/reset-password"
                    element={
                      <RouteChunk>
                        <ResetPassword />
                      </RouteChunk>
                    }
                  />
                  <Route
                    path="auth/sso-callback"
                    element={
                      <RouteChunk>
                        <SSOCallback />
                      </RouteChunk>
                    }
                  />
                  {/* AN-25: after Google/Apple, record or ask for consent, then go on. */}
                  <Route
                    path="auth/sso-complete"
                    element={
                      <RouteChunk>
                        <SSOComplete />
                      </RouteChunk>
                    }
                  />
                </Route>

                {/* `register` is what several other products call this screen, and
                  what our own QA notes call it, while the route here is
                  `/auth/signup`. It was one of the blank pages #1172 measured, so
                  it goes to the real sign-up form rather than to the not-found
                  page. */}
                <Route path="auth/register" element={<Navigate to="/auth/signup" replace />} />

                {/* Onboarding — outside AppLayout (full-screen flow, auth required) */}
                <Route
                  path="/onboarding"
                  element={
                    <ProtectedRoute>
                      <RouteChunk>
                        <Onboarding />
                      </RouteChunk>
                    </ProtectedRoute>
                  }
                />

                {/* Legacy redirects — preserve cached signup callbacks and old links */}
                <Route path="/onboarding/welcome" element={<Navigate to="/onboarding" replace />} />
                <Route
                  path="/onboarding/connect-bank"
                  element={<Navigate to="/onboarding" replace />}
                />
                <Route
                  path="/onboarding/post-connect"
                  element={<Navigate to="/onboarding" replace />}
                />

                {/* Anything no other route claims (#1172). React Router v6 ranks
                  matches by specificity rather than by declaration order, so the
                  catch-all sits here, in the public half of the tree, and still
                  loses to every concrete route below it. Public on purpose: a
                  mistyped address must not send a half-onboarded user back into
                  the wizard, and a signed-out visitor has to be able to read it. */}
                <Route
                  path="*"
                  element={
                    <RouteChunk>
                      <NotFound />
                    </RouteChunk>
                  }
                />

                {/* Main app — gated behind onboarding */}
                <Route element={<OnboardingGate />}>
                  {/* Home replaced the Dashboard (redesign, 2026-10-13), behind the switch:
                    on, /dashboard lands on Home; off, /home lands on the Dashboard. */}
                  <Route
                    path="dashboard"
                    element={
                      <RedesignAwareRoute>
                        <RouteChunk>
                          <Dashboard />
                        </RouteChunk>
                      </RedesignAwareRoute>
                    }
                  />
                  <Route
                    path="home"
                    element={
                      <RedesignAwareRoute>
                        <RouteChunk>
                          <Home />
                        </RouteChunk>
                      </RedesignAwareRoute>
                    }
                  />
                  {/* Anthony's Plan spec: "Your plan", behind the switch. Off, /plan lands on
                    today's Forecast (lib/redesignRoutes REDESIGN_ONLY). */}
                  <Route
                    path="plan"
                    element={
                      <RedesignAwareRoute>
                        <RouteChunk>
                          <Plan />
                        </RouteChunk>
                      </RedesignAwareRoute>
                    }
                  />
                  <Route
                    path="transactions"
                    element={
                      <RouteChunk>
                        <Transactions />
                      </RouteChunk>
                    }
                  />
                  <Route
                    path="rules"
                    element={
                      <RedesignAwareRoute>
                        <RouteChunk>
                          <Rules />
                        </RouteChunk>
                      </RedesignAwareRoute>
                    }
                  />
                  {/* RD-07: Rules in Settings & profile, with the switch on. */}
                  <Route
                    path="settings/rules"
                    element={
                      <RedesignAwareRoute>
                        <RouteChunk>
                          <Rules />
                        </RouteChunk>
                      </RedesignAwareRoute>
                    }
                  />
                  {/* Budgets, Allocate (/forecast) and Goals: retired with the switch on, each
                      lands on Plan (Paul, 2026-10-29); off, prod's pages as they are. */}
                  <Route
                    path="budgets"
                    element={
                      <RedesignAwareRoute>
                        <RouteChunk>
                          <Budgets />
                        </RouteChunk>
                      </RedesignAwareRoute>
                    }
                  />
                  <Route
                    path="forecast"
                    element={
                      <RedesignAwareRoute>
                        <RouteChunk>
                          <Forecast />
                        </RouteChunk>
                      </RedesignAwareRoute>
                    }
                  />
                  <Route
                    path="calendar"
                    element={
                      <RouteChunk>
                        <Calendar />
                      </RouteChunk>
                    }
                  />
                  <Route
                    path="recurring"
                    element={
                      <RouteChunk>
                        <RecurringTransactions />
                      </RouteChunk>
                    }
                  />
                  <Route
                    path="debt"
                    element={
                      <RouteChunk>
                        <DebtEliminator />
                      </RouteChunk>
                    }
                  />
                  <Route
                    path="paycheck"
                    element={
                      <RouteChunk>
                        <PaycheckRemainder />
                      </RouteChunk>
                    }
                  />
                  <Route
                    path="settings"
                    element={
                      <RouteChunk>
                        <Settings />
                      </RouteChunk>
                    }
                  />
                  <Route
                    path="help"
                    element={
                      <RouteChunk>
                        <Help />
                      </RouteChunk>
                    }
                  />
                  <Route
                    path="banks"
                    element={
                      <RouteChunk>
                        <BankManagement />
                      </RouteChunk>
                    }
                  />
                  <Route
                    path="banks/oauth-return"
                    element={
                      <RouteChunk>
                        <PlaidOAuthReturn />
                      </RouteChunk>
                    }
                  />
                  <Route
                    path="goals"
                    element={
                      <RedesignAwareRoute>
                        <RouteChunk>
                          <Goals />
                        </RouteChunk>
                      </RedesignAwareRoute>
                    }
                  />
                  <Route
                    path="insights"
                    element={
                      <RouteChunk>
                        <Insights />
                      </RouteChunk>
                    }
                  />
                  <Route
                    path="financial-analysis"
                    element={
                      <RouteChunk>
                        <FinancialAnalysis />
                      </RouteChunk>
                    }
                  />
                  <Route
                    path="payment"
                    element={
                      <RouteChunk>
                        <Payment />
                      </RouteChunk>
                    }
                  />
                  <Route
                    path="payment/success"
                    element={
                      <RouteChunk>
                        <PaymentSuccess />
                      </RouteChunk>
                    }
                  />
                </Route>
              </PendingConsentRoutes>
              {/* Mounted once inside the router so it rides every route, incl. pre-auth */}
              <FeedbackWidget />
            </RedesignNavProvider>
          </PreferencesProvider>
        </BrowserRouter>
        <Toaster />
        <PendingDeletedNotice />
      </TooltipProvider>
    </QueryClientProvider>
  );
}
